import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

// Re-export so callers using `wallet.Paise` as both value and type work without an extra import.
export { Paise, Id } from '../common/primitives';

/**
 * Wallet contracts — shared between the API and all three SPAs.
 *
 * The wallet is a domain abstraction layered on top of the double-entry ledger.
 * A customer wallet = the sum of {@link WalletCreditSchema} rows still ACTIVE or PARTIALLY_USED,
 * grouped per {@link WalletBucketSchema}. A vendor wallet = the running balance of the vendor's
 * CASH-kind LedgerAccount, less any ACTIVE {@link ReserveHoldSchema}s.
 */

// ── Enums (mirror prisma) ────────────────────────────────────────────────────

export const WalletBucketSchema = z.enum(['CASHBACK', 'REFUND', 'REFERRAL', 'PROMO', 'GENERIC']);
export type WalletBucket = z.infer<typeof WalletBucketSchema>;

export const WalletCreditStatusSchema = z.enum([
  'ACTIVE',
  'PARTIALLY_USED',
  'FULLY_USED',
  'EXPIRED',
  'REVOKED',
]);
export type WalletCreditStatus = z.infer<typeof WalletCreditStatusSchema>;

export const PaymentSplitMethodSchema = z.enum([
  'WALLET_ONLY',
  'WALLET_AND_GATEWAY',
  'GATEWAY_ONLY',
]);
export type PaymentSplitMethod = z.infer<typeof PaymentSplitMethodSchema>;

export const ReserveTypeSchema = z.enum(['REFUND_RISK', 'CHARGEBACK', 'FRAUD_FREEZE']);
export type ReserveType = z.infer<typeof ReserveTypeSchema>;

export const ReserveStatusSchema = z.enum(['ACTIVE', 'RELEASED', 'FORFEITED']);
export type ReserveStatus = z.infer<typeof ReserveStatusSchema>;

export const WalletDisputeStatusSchema = z.enum(['OPEN', 'INVESTIGATING', 'RESOLVED', 'REJECTED']);
export type WalletDisputeStatus = z.infer<typeof WalletDisputeStatusSchema>;

export const LedgerOwnerTypeSchema = z.enum(['PLATFORM', 'VENDOR', 'CUSTOMER', 'AGENT']);
export type LedgerOwnerType = z.infer<typeof LedgerOwnerTypeSchema>;

export const LedgerAccountKindSchema = z.enum([
  'CASH',
  'RECEIVABLE',
  'PAYABLE',
  'INCOME',
  'EXPENSE',
  'HELD',
]);
export type LedgerAccountKind = z.infer<typeof LedgerAccountKindSchema>;

// ── Response shapes ──────────────────────────────────────────────────────────

export const WalletCreditSchema = z.object({
  id: Id,
  bucket: WalletBucketSchema,
  amountPaise: Paise,
  remainingPaise: Paise,
  status: WalletCreditStatusSchema,
  expiresAt: IsoDateTime.nullable(),
  sourceRefType: z.string().nullable(),
  sourceRefId: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type WalletCredit = z.infer<typeof WalletCreditSchema>;

export const WalletBucketBalanceSchema = z.object({
  bucket: WalletBucketSchema,
  spendablePaise: Paise,
  /// Credits expiring within 30 days, for the "expiring soon" warning UI.
  expiringSoonPaise: Paise,
});
export type WalletBucketBalance = z.infer<typeof WalletBucketBalanceSchema>;

export const CustomerWalletSummarySchema = z.object({
  userId: Id,
  totalSpendablePaise: Paise,
  buckets: z.array(WalletBucketBalanceSchema),
  /// Most recent credits first, capped server-side.
  recentCredits: z.array(WalletCreditSchema),
});
export type CustomerWalletSummary = z.infer<typeof CustomerWalletSummarySchema>;

export const VendorWalletSummarySchema = z.object({
  vendorId: Id,
  /// Cash balance (CASH-kind ledger account balance), inclusive of all incoming receivables already moved to cash.
  cashPaise: Paise,
  /// Sum of ACTIVE reserve holds across all types.
  heldPaise: Paise,
  /// cashPaise minus heldPaise — what the vendor can request as payout right now.
  withdrawablePaise: Paise,
  /// Breakdown of holds by type, for vendor-portal transparency.
  holdsByType: z.array(z.object({ type: ReserveTypeSchema, paise: Paise })),
  activeHolds: z
    .array(
      z.object({
        id: Id,
        type: ReserveTypeSchema,
        amountPaise: Paise,
        reason: z.string().nullable(),
        createdAt: IsoDateTime,
      }),
    )
    .optional(),
});
export type VendorWalletSummary = z.infer<typeof VendorWalletSummarySchema>;

export const LedgerEntrySchema = z.object({
  id: Id,
  transactionId: Id,
  accountId: Id,
  debitPaise: Paise,
  creditPaise: Paise,
  createdAt: IsoDateTime,
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

export const WalletTransactionSchema = z.object({
  id: Id,
  kind: z.string(),
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  memo: z.string().nullable(),
  postedAt: IsoDateTime,
  /// The ledger entries this transaction created, scoped to accounts the caller is allowed to see.
  entries: z.array(LedgerEntrySchema),
});
export type WalletTransaction = z.infer<typeof WalletTransactionSchema>;

export const ReserveHoldSchema = z.object({
  id: Id,
  vendorId: Id,
  type: ReserveTypeSchema,
  amountPaise: Paise,
  status: ReserveStatusSchema,
  scheduledReleaseAt: IsoDateTime.nullable(),
  releasedAt: IsoDateTime.nullable(),
  reason: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ReserveHold = z.infer<typeof ReserveHoldSchema>;

export const WalletDisputeSchema = z.object({
  id: Id,
  userId: Id,
  disputedTxId: Id,
  correctionTxId: Id.nullable(),
  reason: z.string(),
  description: z.string().nullable(),
  status: WalletDisputeStatusSchema,
  resolutionNote: z.string().nullable(),
  resolvedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type WalletDispute = z.infer<typeof WalletDisputeSchema>;

export const PaymentSplitQuoteSchema = z.object({
  totalPaise: Paise,
  walletPaise: Paise,
  gatewayPaise: Paise,
  method: PaymentSplitMethodSchema,
  /// Per-credit breakdown of which credits will be debited and by how much.
  walletAllocations: z.array(
    z.object({
      creditId: Id,
      bucket: WalletBucketSchema,
      paise: Paise,
    }),
  ),
});
export type PaymentSplitQuote = z.infer<typeof PaymentSplitQuoteSchema>;

// ── Request shapes ───────────────────────────────────────────────────────────

export const IssueCreditRequest = z
  .object({
    userId: Id,
    bucket: WalletBucketSchema,
    amountPaise: Paise.refine((v) => v > 0, { message: 'Must be positive' }),
    expiresAt: IsoDateTime.optional(),
    sourceRefType: z.string().trim().max(64).optional(),
    sourceRefId: z.string().trim().max(64).optional(),
    note: z.string().trim().max(500).optional(),
    /// Idempotency-Key value, propagated from the request header for safety.
    idempotencyKey: z.string().trim().min(8).max(128).optional(),
  })
  .strict();
export type IssueCreditRequest = z.infer<typeof IssueCreditRequest>;

export const QuoteWalletPaymentRequest = z
  .object({
    /// What the caller wants to pay in total. Wallet will cover up to maxWalletPaise (or all).
    totalPaise: Paise.refine((v) => v > 0, { message: 'Must be positive' }),
    /// Cap how much wallet the caller is willing to apply (optional — defaults to "all available").
    maxWalletPaise: Paise.optional(),
    /// Restrict to specific buckets (e.g. only spend REFUND credits). Defaults to all buckets.
    allowedBuckets: z.array(WalletBucketSchema).optional(),
  })
  .strict();
export type QuoteWalletPaymentRequest = z.infer<typeof QuoteWalletPaymentRequest>;

export const ChargeWalletRequest = z
  .object({
    orderGroupId: Id,
    totalPaise: Paise.refine((v) => v > 0, { message: 'Must be positive' }),
    maxWalletPaise: Paise.optional(),
    allowedBuckets: z.array(WalletBucketSchema).optional(),
    /// Payment row id from the commerce module, if the order needs a gateway leg.
    paymentRefId: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type ChargeWalletRequest = z.infer<typeof ChargeWalletRequest>;

export const RaiseDisputeRequest = z
  .object({
    disputedTxId: Id,
    reason: z.string().trim().min(4).max(200),
    description: z.string().trim().max(2000).optional(),
  })
  .strict();
export type RaiseDisputeRequest = z.infer<typeof RaiseDisputeRequest>;

export const ResolveDisputeRequest = z
  .object({
    status: z.enum(['RESOLVED', 'REJECTED']),
    resolutionNote: z.string().trim().min(4).max(2000),
    /// If RESOLVED with a correction, the amount to credit the user. Optional.
    correctionPaise: Paise.optional(),
    correctionBucket: WalletBucketSchema.optional(),
  })
  .strict()
  .refine((v) => !(v.correctionPaise && !v.correctionBucket), {
    message: 'correctionBucket is required when correctionPaise is set',
    path: ['correctionBucket'],
  });
export type ResolveDisputeRequest = z.infer<typeof ResolveDisputeRequest>;

export const CreateReserveHoldRequest = z
  .object({
    vendorId: Id,
    type: ReserveTypeSchema,
    amountPaise: Paise.refine((v) => v > 0),
    scheduledReleaseAt: IsoDateTime.optional(),
    sourceRefType: z.string().trim().max(64).optional(),
    sourceRefId: z.string().trim().max(64).optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type CreateReserveHoldRequest = z.infer<typeof CreateReserveHoldRequest>;

export const ReleaseReserveHoldRequest = z
  .object({
    /// 'RELEASE' returns funds to vendor cash; 'FORFEIT' debits the held funds to an expense bucket.
    action: z.enum(['RELEASE', 'FORFEIT']),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type ReleaseReserveHoldRequest = z.infer<typeof ReleaseReserveHoldRequest>;

// ── Redemption at checkout (reward-coin style) ───────────────────────────────

/**
 * What a customer may put towards one order, and why that is the figure.
 *
 * Shown at checkout, so the numbers have to explain themselves: a shopper with
 * ₹500 of cashback looking at a ₹1,000 order needs to know why only ₹100 is
 * offered, and that the rest has not gone anywhere.
 */
export const WalletRedemptionQuoteSchema = z.object({
  totalPaise: Paise,
  /// The most the wallet can cover on this order — what the app should offer.
  usablePaise: Paise,
  /// Spendable reward balance (cashback, referral, promo), before the cap.
  rewardBalancePaise: Paise,
  /// The ceiling the cap puts on reward money for an order this size.
  rewardCapPaise: Paise,
  /// The customer's own money — top-ups and refunds — which the cap never limits.
  ownBalancePaise: Paise,
  /// The cap as a percentage, for wording like "up to 10% with coins".
  maxPercentOfOrder: z.number().int().min(0).max(100),
  /// False when redemption is switched off, or the order is below the floor.
  enabled: z.boolean(),
});
export type WalletRedemptionQuote = z.infer<typeof WalletRedemptionQuoteSchema>;

export const WalletRedemptionQuoteRequest = z
  .object({
    totalPaise: Paise.refine((v) => v > 0, { message: 'Must be positive' }),
  })
  .strict();
export type WalletRedemptionQuoteRequest = z.infer<typeof WalletRedemptionQuoteRequest>;

// ── Top-up (customer adds their own money) ───────────────────────────────────

export const WalletTopupStatusSchema = z.enum(['CREATED', 'CREDITING', 'PAID', 'FAILED']);
export type WalletTopupStatus = z.infer<typeof WalletTopupStatusSchema>;

/**
 * Sensible bounds on a self-service top-up.
 *
 * The floor keeps gateway fees from exceeding the amount loaded; the ceiling is
 * a blast radius, not a product limit — a top-up is irreversible once credited,
 * so a fat-fingered extra zero should be refused rather than refunded.
 */
export const MIN_TOPUP_PAISE = 1_000; // ₹10
export const MAX_TOPUP_PAISE = 10_000_000; // ₹1,00,000

export const StartWalletTopupRequest = z
  .object({
    amountPaise: Paise.refine((v) => v >= MIN_TOPUP_PAISE && v <= MAX_TOPUP_PAISE, {
      message: 'Top-up must be between ₹10 and ₹1,00,000',
    }),
  })
  .strict();
export type StartWalletTopupRequest = z.infer<typeof StartWalletTopupRequest>;

/** Everything the client SDK needs to open the gateway sheet for a top-up. */
export const WalletTopupInitSchema = z.object({
  topupId: Id,
  /// Razorpay key id — safe to expose to the client.
  keyId: z.string().nullable(),
  providerOrderId: z.string().nullable(),
  amountPaise: Paise,
  currency: z.literal('INR'),
  nextAction: z.literal('OPEN_RAZORPAY_CHECKOUT'),
});
export type WalletTopupInit = z.infer<typeof WalletTopupInitSchema>;

/** The three values the gateway hands back to the client on success. */
export const VerifyWalletTopupRequest = z
  .object({
    razorpayOrderId: z.string().trim().min(1).max(64),
    razorpayPaymentId: z.string().trim().min(1).max(64),
    razorpaySignature: z.string().trim().min(1).max(256),
  })
  .strict();
export type VerifyWalletTopupRequest = z.infer<typeof VerifyWalletTopupRequest>;

/**
 * The outcome of a top-up as the app should present it.
 *
 * `PENDING` is not a failure: the signature checked out but the credit had not
 * landed yet, so the app should say the money is on its way rather than that
 * something went wrong. The webhook finishes it either way.
 */
export const WalletTopupResultSchema = z.object({
  topupId: Id,
  status: WalletTopupStatusSchema,
  creditedPaise: Paise,
  /// The wallet credit this became, once it exists.
  creditId: Id.nullable(),
});
export type WalletTopupResult = z.infer<typeof WalletTopupResultSchema>;

/**
 * One line in the customer's passbook.
 *
 * Derived from the ledger, not from the wallet credits: a credit row says money
 * arrived, and says nothing about it being spent. The passbook has to show both
 * sides, so it reads the double-entry journal.
 */
export const WalletPassbookEntrySchema = z.object({
  id: Id,
  transactionId: Id,
  kind: z.string(),
  memo: z.string().nullable(),
  debitPaise: Paise,
  creditPaise: Paise,
  refType: z.string().nullable(),
  refId: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type WalletPassbookEntry = z.infer<typeof WalletPassbookEntrySchema>;
